import {
    SliceFactory,
    FetchCollectionData,
    FetchItemDataType,
    ApiCollectionResponse,
    DataSource,
    Order,
} from '@europrocurement/l2d-redux-utils';
import {
    ConfigurationParameters,
    Configuration,
    SousClientsApi,
    TiersTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
} from '../../../openApi/ApiTiers';
import { SOUS_CLIENT_SLICE_NAME, TIERS_REDUCER_NAME } from '../constants';

export type SousClientApi = TiersTiersJsonldTiersReadNoteGroupTimestampableBlameambleGroup &
    Record<string, unknown>;

export type SousClient = SousClientApi;

type ElasticClause =
    | { match: Record<string, string | number | boolean> }
    | { terms: Record<string, Array<string | number>> }
    | { query_string: { fields: string[]; query: string } }
    | { bool: { must: ElasticClause[] } };

type BoolQuery = {
    must?: ElasticClause[];
    should?: ElasticClause[];
    minimum_should_match?: number;
};

const mapperSousClient = function (sousClient: SousClientApi) {
    return {
        ...sousClient,
    } as SousClient;
};
function cleanObject<T>(obj: T): T {
    if (Array.isArray(obj)) {
        return obj
            .map((value) => cleanObject(value))
            .filter(
                (value) =>
                    value !== undefined &&
                    value !== null &&
                    value !== '' &&
                    !(Array.isArray(value) && value.length === 0),
            ) as unknown as T;
    }
    if (typeof obj === 'object' && obj !== null) {
        const newObj = {} as T;
        Object.entries(obj).forEach(([key, value]) => {
            const cleaned = cleanObject(value);
            const isEmptyObject =
                typeof cleaned === 'object' &&
                cleaned !== null &&
                Object.keys(cleaned).length === 0;

            if (
                cleaned !== undefined &&
                cleaned !== null &&
                cleaned !== '' &&
                !(Array.isArray(cleaned) && cleaned.length === 0) &&
                !isEmptyObject
            ) {
                newObj[key] = cleaned;
            }
        });
        return newObj;
    }

    return obj;
}

function filterQuery(
    filters: DataSource<Record<string, unknown>>['filters'],
    aditionalMatch: ElasticClause[] = [],
    orders: DataSource<Order[]>['orders'] = [],
    ordersWithoutKeyword: Array<string> = [],
) {
    const mustParts: ElasticClause[] = [];

    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            mustParts.push({ terms: { [key]: value } });
        } else if (typeof value === 'boolean' || typeof value === 'number') {
            mustParts.push({ match: { [key]: value } });
        } else if (typeof value === 'string') {
            mustParts.push({
                query_string: {
                    fields: [`${key}`],
                    query: `${value}`,
                },
            });
        }
    });

    const bool: BoolQuery = {};

    if (mustParts.length > 0) {
        bool.must = mustParts;
    }

    if (Array.isArray(aditionalMatch) && aditionalMatch.length > 0) {
        bool.should = aditionalMatch;
        bool.minimum_should_match = 0; // permet le retour même sans match sur le texte
    }

    const query: {
        query: { bool: Record<string, unknown> };
        sort?: Array<Record<string, unknown>>;
    } = { query: { bool } };

    if (orders && orders.length > 0) {
        query.sort = orders.map((order: Order) => {
            const isDateField = order.field.toLowerCase().includes('date');
            const field =
                isDateField || ordersWithoutKeyword.includes(order.field)
                    ? order.field
                    : `${order.field}.keyword`;

            return {
                [field]: {
                    order: order.value,
                },
            };
        });
    }

    return JSON.stringify(query);
}

export function createSousClientSlice(configuration: ConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new SousClientsApi(conf);

    const fetchSousClientsCollection: FetchCollectionData<SousClientApi> = ({
        filters,
        pagination,
        orders,
        xIdSociete,
        options,
        search,
    }) => {
        let currSearch = '';

        if (search && search !== '') {
            currSearch = search;
            if (!Number.isNaN(Number(search.replace(/\s+/g, '')))) {
                currSearch = search.replace(/\s+/g, '');
            }
        }

        let additionalShouldConstraint: Array<{
            term: { id: number | string };
        }> = [];

        if (filters.orSousClientId) {
            additionalShouldConstraint = (filters.orSousClientId as Array<number | string>).map(
                (id) => ({
                    term: { id },
                }),
            );
        }

        if (filters?.raisonSociale !== undefined && (filters?.raisonSociale as string) !== '') {
            currSearch = filters.raisonSociale as string;
        }

        const filterFields = {
            'objectgroups.id': filters?.objectgroups,
        };

        let fields: Array<string> = ['raisonSociale', 'siren', 'siret'];

        // Recherche Global + recherche dans le formulaires formalité
        if (!filters.objectgroups && !filters.raisonSociale) {
            fields = ['contacts.emailsContact.email', ...fields];
        }

        const shouldConditions: Array<Record<string, unknown>> = [];

        if (currSearch.trim() !== '') {
            shouldConditions.push({
                query_string: {
                    query: `**${currSearch}**`,
                    fields,
                },
            });
        }

        shouldConditions.push(...additionalShouldConstraint);

        const mustFilters = {
            isSousclient: true,
            idPrescripteur: filters?.prescripteurId,
            ...Object.entries(filterFields).reduce(
                (acc, [key, value]) => {
                    if (value !== undefined && value !== null) acc[key] = value;
                    return acc;
                },
                {} as Record<string, unknown>,
            ),
        };
        const ordersQuery = orders.map((item) => ({
            ...item,
            field: item.field,
        }));

        const fieldNotKeywordSort: Array<string> = ['personnePhysique', 'nbDossier'];

        const opensearchQuery = filterQuery(
            mustFilters,
            shouldConditions,
            ordersQuery,
            fieldNotKeywordSort,
        );

        return api
            .getListTiersTiersCollection(
                {
                    xIdSociete,
                    page: pagination.page + 1,
                    itemsPerPage: pagination.itemsPerPage,
                    search: cleanObject(opensearchQuery),
                },
                options,
            )
            .then((res) => res.data) as Promise<ApiCollectionResponse<SousClientApi>>;
    };

    const fetchSousClientsItem: FetchItemDataType<SousClientApi> = ({
        idItem,
        xIdSociete,
        options,
    }) =>
        api
            .getTiersTiersItem(
                {
                    id: `${idItem}`,
                    xIdSociete,
                },
                options,
            )
            .then((res) => res.data) as Promise<SousClientApi>;

    const { slice: sousClientSlice, dataSourcesThunks: sousClientDataSourcesThunks } =
        SliceFactory.createSlice<SousClientApi, SousClient>(
            SOUS_CLIENT_SLICE_NAME,
            TIERS_REDUCER_NAME,
            ['export'],
            fetchSousClientsCollection,
            fetchSousClientsItem,
            mapperSousClient,
            {
                isSousclient: true,
                demo: false,
                deleted: false,
            },
            {},
            [],
        );

    return { sousClientSlice, sousClientDataSourcesThunks };
}
